/** @format */
// app-routing.module.ts
import { NgModule } from "@angular/core"
import { RouterModule, Routes } from "@angular/router"
import { HomeComponent } from "./pages/home/home.component"
import { AnalyticsComponent } from "./pages/analytics/analytics.component"
import { ReportsComponent } from "./pages/reports/reports.component"
import { SettingsComponent } from "./pages/settings/settings.component"
import { DashboardComponent } from "./pages/dashboard/dashboard.component"
import { CompanyComponent } from "./components/settings/company/company.component"
import { AddCompanyComponent } from "./components/settings/add-company/add-company.component"
import { EditCompanyComponent } from "./components/settings/edit-company/edit-company.component"
import { PlantComponent } from "./components/settings/plant/plant.component"
import { AuthorisatonErrorComponent } from "./pages/authorisaton-error/authorisaton-error.component"
import { AddPlantComponent } from "./components/settings/add-plant/add-plant.component"
import { EditPlantComponent } from "./components/settings/edit-plant/edit-plant.component"
import { DataPointComponent } from "./components/settings/data-point/data-point.component"
import { AddDataPointComponent } from "./components/settings/add-data-point/add-data-point.component"
import { EditDataPointComponent } from "./components/settings/edit-data-point/edit-data-point.component"
import { ConfigureNotificationsComponent } from "./components/settings/configure-notifications/configure-notifications.component"
import { NotificationsConfigComponent } from "./components/notification-config/notifications-config.component"
import { AddUnitComponent } from "./components/settings/add-unit/add-unit.component"
import { UnitComponent } from "./components/settings/unit/unit.component"
import { EditUnitComponent } from "./components/settings/edit-unit/edit-unit.component"
import { LogsheetComponent } from "./components/settings/logsheet/logsheet.component"
import { AddNonAdminGroupComponent } from "./components/settings/add-group/add-non-admin-group.component"
import { GroupsComponent } from "./components/settings/groups/groups.component"
import { AddLogsheetComponent } from "./components/settings/add-logsheet/add-logsheet.component"
import { ScheduleLogsheetComponent } from "./components/settings/schedule-logsheet/schedule-logsheet.component"
import { EditLogsheetComponent } from "./components/settings/edit-logsheet/edit-logsheet.component"
import { ModificationWindowComponent } from "./components/settings/modification-window/modification-window.component"
import { AssignLogsheetComponent } from "./components/settings/assign-logsheet/assign-logsheet.component"
import { AddSuperUserComponent } from "./components/settings/add-super-user/add-super-user.component"
import { AddAdminUserComponent } from "./components/settings/add-admin-user/add-admin-user.component"
import { UserComponent } from "./components/settings/user/user.component"
import { AddNonAdminUserComponent } from "./components/settings/add-non-admin-user/add-non-admin-user.component"
import { EditNonAdminUserComponent } from "./components/settings/edit-non-admin-user/edit-non-admin-user.component"
import { ProfileComponent } from "./components/settings/profile/profile.component"
import { EditAdminUserComponent } from "./components/settings/edit-admin-user/edit-admin-user.component"
import { AuditComponent } from "./components/settings/audit/audit.component"
import { AccountCompleteComponent } from "./pages/account-complete/account-complete.component"
import { OverviewComponent } from "./pages/overview/overview.component"
import { UnitLogComponent } from "./components/overview/unit-log/unit-log.component"
import { UnitLogEmptyComponent } from "./components/overview/unit-log-empty/unit-log-empty.component"
import { LogSheetFormComponent } from "./components/home/log-sheet-form/log-sheet-form.component"
import { NonAdminHomeComponent } from "./pages/non-admin-home/non-admin-home.component"
import { RoleGroups, Roles } from "./models/roles"
import { RoleGuard } from "./guards/role-guard"
import { SuperAdminGuard } from "./guards/super-admin.guard"
import { AddTempUserComponent } from "./components/settings/add-temp-user/add-temp-user.component"
import { AddTempGroupComponent } from "./components/settings/add-temp-group/add-temp-group.component"
import { AssignTempFormComponent } from "./components/settings/assign-temp-form/assign-temp-form.component"
import { PublicFormComponent } from "./components/public-form/public-form.component"
import { CloudStorageComponent } from "./components/settings/cloud-storage/cloud-storage.component"
import { IntegrationComponent } from "./integration.component/integration.component"
import { AuthChildGuard, AuthGuard } from "./guards/auth.guard"
import { WorkflowComponent } from "./components/settings/workflow/workflow.component"
import { WorkflowExceptionsComponent } from "./components/settings/workflow-exceptions/workflow-exceptions.component"
import { WorkflowBuilderComponent } from "./components/settings/workflows/workflow-builder.component"
import { WorkflowUnsavedChangesGuard } from "./guards/workflow-unsaved-changes.guard"
import { WorkflowRuntimeFormComponent } from "./components/workflow-runtime-form/workflow-runtime-form.component"
import { WorkflowLinkLandingComponent } from "./components/workflow-link-landing/workflow-link-landing.component"

const routes: Routes = [
	{
		path: "auth/login",
		redirectTo: "dashboard",
		pathMatch: "full",
	},
	{
		path: "public-form/:token",
		component: PublicFormComponent
	},
	// Guest access: both routes must sit outside the AuthGuard, or a link recipient with no
	// account is bounced to Keycloak before the page can offer them the OTP flow. The landing
	// page forwards signed-in users into the app itself.
	{
		path: "workflow/link/:token",
		component: WorkflowLinkLandingComponent,
	},
	{
		path: "workflow/guest",
		component: WorkflowRuntimeFormComponent,
		data: { guest: true },
	},
	{
		path: "auth/resetPassword/:token",
		redirectTo: "dashboard",
	},
	{
		path: "forgot-password",
		redirectTo: "dashboard",
		pathMatch: "full",
	},
	{
		path: "account/verify-email",
		redirectTo: "dashboard",
		pathMatch: "full",
	},
	{
		path: "account-complete",
		component: AccountCompleteComponent,
	},
	{
		path: "",
		redirectTo: "dashboard",
		pathMatch: "full",
	},
	{
		path: "",
		component: DashboardComponent,
		canActivate: [AuthGuard],
		canActivateChild: [AuthChildGuard, RoleGuard],
		children: [
			{
				path: "home",
				component: HomeComponent,
				data: { roles: RoleGroups.superAdminAndAdmin },
				canActivateChild: [RoleGuard],
				children: [
					{
						path: "",
						redirectTo: "home",
						pathMatch: "full",
						data: { roles: RoleGroups.superAdminAndAdmin },
					},
					{
						path: "overview",
						component: OverviewComponent,
						data: { roles: RoleGroups.superAdminAndAdmin },
						canActivateChild: [RoleGuard],
						children: [
							{
								path: "branch",
								component: UnitLogEmptyComponent,
								data: { roles: RoleGroups.superAdminAndAdmin }
							},
							{
								path: "branch/:id",
								component: UnitLogComponent,
								data: { roles: RoleGroups.superAdminAndAdmin }
							},
							{
								path: "branch/:id/forms",
								component: LogSheetFormComponent,
								data: { roles: RoleGroups.superAdminAndAdmin }
							},
						],
					},
				],
			},
			{
				//Non-admin dashboard
				path: "dashboard",
				component: NonAdminHomeComponent,
				data: { roles: RoleGroups.operatorOnly }
			},
			{
				path: "workflow-runtime/definitions/:workflowVersionId",
				component: WorkflowRuntimeFormComponent,
				data: { roles: RoleGroups.allRoles }
			},
			{
				path: "workflow-runtime/instances/:workflowItemId",
				component: WorkflowRuntimeFormComponent,
				data: { roles: RoleGroups.allRoles }
			},
			{
				path: "workflow/item/:workflowItemId",
				redirectTo: "workflow-runtime/instances/:workflowItemId",
				pathMatch: "full"
			},
			{
				path: "form-details/:id",
				component: LogSheetFormComponent,
				data: { roles: RoleGroups.operatorOnly }
			},
			{
				path: "analytics",
				component: AnalyticsComponent,
				data: { roles: RoleGroups.superAdminAndAdmin }
			},
			{
				path: "reports",
				component: ReportsComponent,
				data: { roles: RoleGroups.superAdminAndAdmin }
			},
			{
				path: "settings",
				component: SettingsComponent,
				data: { roles: RoleGroups.allRoles },
				canActivateChild: [RoleGuard],
				children: [
					{
						path: "",
						redirectTo: "tenant",
						pathMatch: "full",
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "tenant",
						component: CompanyComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "tenant/add-tenant",
						component: AddCompanyComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "tenant/edit-tenant/:id",
						component: EditCompanyComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "form-fields/configure-notifications/:id",
						component: ConfigureNotificationsComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "subsidiaries",
						component: PlantComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "subsidiaries/add-subsidiary",
						component: AddPlantComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "subsidiaries/edit-subsidiary/:id",
						component: EditPlantComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "form-fields",
						component: DataPointComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "form-fields/add-form-field",
						component: AddDataPointComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "form-fields/edit-form-field/:id",
						component: EditDataPointComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "branch",
						component: UnitComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "integrations/api-connections",
						component: IntegrationComponent,
						data: { roles: RoleGroups.adminOnly }
					},
					{
						path: "integrations/cloud-connections",
						component: CloudStorageComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "subsidiaries/edit-subsidiary/:id",
						component: EditPlantComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "branch/add-branch",
						component: AddUnitComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "branch/edit-branch/:id",
						component: EditUnitComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "forms/configure-notifications/:id",
						component: NotificationsConfigComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "forms",
						component: LogsheetComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "groups/add-group",
						component: AddNonAdminGroupComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "groups/add-temp-group",
						component: AddTempGroupComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "groups",
						component: GroupsComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "forms/add-form",
						component: AddLogsheetComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "forms/schedule-form/:id",
						component: ScheduleLogsheetComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "forms/edit-form/:id",
						component: EditLogsheetComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "modification-window",
						component: ModificationWindowComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "forms/assign-form/:id",
						component: AssignLogsheetComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "forms/assign-temp-form/:id",
						component: AssignTempFormComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "user/add-super-user",
						component: AddSuperUserComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "user/add-admin-user",
						component: AddAdminUserComponent,
						data: { roles: RoleGroups.superAdminOnly }
					},
					{
						path: "user",
						component: UserComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "user/add-non-admin-user",
						component: AddNonAdminUserComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "user/edit-non-admin-user",
						component: EditNonAdminUserComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "user/edit-temporary-user",
						component: EditNonAdminUserComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},
					{
						path: "user/edit-admin-user",
						component: EditAdminUserComponent,
						data: { roles: RoleGroups.superAdminOnly }
					},
					{
						path: "user/add-temp-user",
						component: AddTempUserComponent,
						data: { roles: RoleGroups.superAdminAndAdmin }
					},

					{
						path:"integrations",
						component:IntegrationComponent,
						data:{roles:RoleGroups.adminOnly}

					},
					{
						path: "workflows/create-workflow",
						component: WorkflowComponent,
						data: { roles: RoleGroups.superAdminOnly }
					},
					{
						path: "workflows/build",
						component: WorkflowBuilderComponent,
						canDeactivate: [WorkflowUnsavedChangesGuard],
						data: { roles: RoleGroups.superAdminOnly }
					},
					{
						path: "workflows/exceptions",
						component: WorkflowExceptionsComponent,
						data: { roles: RoleGroups.superAdminOnly }
					},
					{
						path: "workflows",
						component: WorkflowComponent,
						data: { roles: RoleGroups.superAdminOnly }
					},
					{
						path: "profile",
						component: ProfileComponent,
						data: { roles: RoleGroups.allRoles }
					},
					{
						path: "audit",
						component: AuditComponent,
						data: { roles: RoleGroups.superAdminAndAdmin },
						canActivate: [SuperAdminGuard],
					},
				],
			},
		],
	},
	{
		path: "error",
		component: AuthorisatonErrorComponent,
	},
]
@NgModule({
	imports: [
		RouterModule.forRoot(routes, {
			useHash: true,
			canceledNavigationResolution: "computed",
		}),
	],
	exports: [RouterModule],
})
export class AppRoutingModule { }
